/**
 * Formulário de Carregamento e Entrega (A4 retrato, página 7 do PDF de
 * referência). Formulário de campo: só Cliente, Lote e os nomes dos
 * ambientes vêm do lote; o resto a equipe preenche à caneta.
 */
import React from "react";
import styled from "styled-components";
import { FiCamera } from "react-icons/fi";

import { FONTE_TITULO } from "./fontes";
import { LogoDaUnidade } from "./FolhaSetor";
import { BlocoTitulo, Cabecalho, Divisor, Observacoes, PAPEL, Papel, Pauta, Ponto, Rotulo, Sobretitulo, Titulo } from "./styles";

const GRAFITE = "#4A4542";
const GRAFITE_ESCURO = "#2E2A28";
const FUNDO_NAO = "#F5F3F1";
const VERMELHO_FOTO = "#B3132E";
const COLUNAS_DE_AMBIENTES = 4;
const LINHAS_DE_AMBIENTES = 5;

const VERIFICACAO_FABRICA = [
  "Possui o projeto impresso?",
  "Possui salva-piso?",
  "Possui cantoneira de proteção?",
  "Realizou a conferência final?",
  "Possui o romaneio de entrega?",
  "Possui checklist de término de montagem?",
];

const VERIFICACAO_OBRA = [
  { texto: "Vídeo do local antes da entrega?" },
  { texto: "Piso protegido?" },
  { texto: "Portas protegidas?" },
  { texto: "Tirou foto da caixa de ferragem?" },
  { texto: "Tirou foto do projeto?" },
  { texto: "Vídeo do imóvel depois da entrega?" },
  { texto: "Fotografou o volume no ambiente?" },
  { texto: "Existem avarias nas paredes?", fotografar: true },
  { texto: "Existem avarias nas portas?", fotografar: true },
];

const exato = "print-color-adjust: exact; -webkit-print-color-adjust: exact;";

const Pilula = styled.div`
  ${exato};
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 999px;
  background: ${PAPEL.tinta};
  color: #fff;
  font-family: ${FONTE_TITULO};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
`;

const FaixaDeCampos = styled.section`
  flex: 0 0 56px;
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1.2fr;
  border: 1px solid ${PAPEL.bordaExterna};
  border-radius: 8px;
  overflow: hidden;

  > div {
    padding: 9px 16px;
    min-width: 0;
  }

  > div + div {
    border-left: 1px solid ${PAPEL.bordaInterna};
  }

  .valor {
    margin-top: 4px;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .tracos {
    margin-top: 6px;
    font-size: 12px;
    color: ${PAPEL.rotulo};
    letter-spacing: 0.08em;
  }
`;

const Secao = styled.section`
  flex: 0 0 auto;
  border: 1px solid ${PAPEL.bordaExterna};
  border-radius: 8px;
  overflow: hidden;
`;

const BarraDaSecao = styled.div`
  ${exato};
  display: grid;
  grid-template-columns: ${({ $comSimNao }) => ($comSimNao ? "minmax(0,1fr) 68px 68px" : "minmax(0,1fr) auto")};
  align-items: stretch;
  min-height: 30px;
  background: ${GRAFITE};
  color: #fff;

  .titulo {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 16px;
    font-family: ${FONTE_TITULO};
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .nota {
    display: flex;
    align-items: center;
    padding: 0 16px;
    font-size: 11px;
  }

  .sim,
  .nao {
    ${exato};
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: ${FONTE_TITULO};
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
  }

  .nao {
    background: ${GRAFITE_ESCURO};
  }
`;

const Caixa = styled.span`
  flex: 0 0 auto;
  display: inline-block;
  width: ${({ $tamanho }) => $tamanho}px;
  height: ${({ $tamanho }) => $tamanho}px;
  border: 1.5px solid ${GRAFITE};
  border-radius: 4px;
  background: #fff;
`;

const GradeDeAmbientes = styled.div`
  display: grid;
  grid-template-columns: repeat(${COLUNAS_DE_AMBIENTES}, minmax(0, 1fr));

  > div {
    min-height: 30px;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 12px;
    font-size: 12px;
    border-top: 1px solid ${PAPEL.bordaInterna};
    min-width: 0;
  }

  > div:not(:nth-child(${COLUNAS_DE_AMBIENTES}n + 1)) {
    border-left: 1px solid ${PAPEL.bordaInterna};
  }

  .nome {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Pergunta = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 68px 68px;
  min-height: 25px;
  border-top: 1px solid ${PAPEL.bordaInterna};
  font-size: 13px;

  .texto {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 0 16px;
  }

  .sim,
  .nao {
    ${exato};
    display: flex;
    align-items: center;
    justify-content: center;
    border-left: 1px solid ${PAPEL.bordaInterna};
  }

  .nao {
    background: ${FUNDO_NAO};
  }
`;

const EtiquetaFoto = styled.span`
  ${exato};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  background: ${VERMELHO_FOTO};
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
`;

const Assinaturas = styled.footer`
  flex: 0 0 54px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  > div {
    border: 1px solid ${PAPEL.bordaExterna};
    border-radius: 8px;
    padding: 10px 16px;
  }
`;

function CelulasDeAmbiente({ ambientes }) {
  const total = Math.max(COLUNAS_DE_AMBIENTES * LINHAS_DE_AMBIENTES, Math.ceil(ambientes.length / COLUNAS_DE_AMBIENTES) * COLUNAS_DE_AMBIENTES);
  return Array.from({ length: total }, (_, i) => (
    <div key={i}>
      <Caixa $tamanho={16} />
      <span className="nome">{ambientes[i] || ""}</span>
    </div>
  ));
}

function ListaDePerguntas({ perguntas }) {
  return perguntas.map((pergunta) => (
    <Pergunta key={pergunta.texto}>
      <div className="texto">
        <span>{pergunta.texto}</span>
        {pergunta.fotografar && (
          <EtiquetaFoto>
            <FiCamera size={12} aria-hidden="true" /> Se sim, fotografar
          </EtiquetaFoto>
        )}
      </div>
      <div className="sim"><Caixa $tamanho={18} /></div>
      <div className="nao"><Caixa $tamanho={18} /></div>
    </Pergunta>
  ));
}

export default function FolhaCarregamento({ folha, logo, via }) {
  const { lote } = folha;
  return (
    <Papel $retrato data-retrato="" data-folha={folha.chave} aria-label={`Carregamento e entrega, lote ${lote.rotulo}`} style={{ color: GRAFITE }}>
      <Cabecalho>
        <LogoDaUnidade logo={logo} />
        <Divisor aria-hidden="true" />
        <BlocoTitulo style={{ maxWidth: "none" }}>
          <Sobretitulo $cor={GRAFITE}>Expedição · Obra</Sobretitulo>
          <Titulo $tamanho={30}>Carregamento e entrega</Titulo>
        </BlocoTitulo>
        <Pilula>
          <Ponto aria-hidden="true" /> Formulário de campo
        </Pilula>
      </Cabecalho>

      <FaixaDeCampos>
        <div><Rotulo>Equipe</Rotulo></div>
        <div><Rotulo>Cliente</Rotulo><div className="valor">{folha.cliente}</div></div>
        <div><Rotulo>Lote</Rotulo><div className="valor">{lote.numero}</div></div>
        <div><Rotulo>Data</Rotulo><div className="tracos">___/___/______</div></div>
      </FaixaDeCampos>

      <Secao>
        <BarraDaSecao>
          <div className="titulo"><span>01</span> Ambientes</div>
          <div className="nota">Escreva o nome de cada ambiente e marque quando carregado</div>
        </BarraDaSecao>
        <GradeDeAmbientes>
          <CelulasDeAmbiente ambientes={folha.ambientes} />
        </GradeDeAmbientes>
      </Secao>

      <Secao>
        <BarraDaSecao $comSimNao>
          <div className="titulo"><span>02</span> Verificação em fábrica</div>
          <div className="sim">Sim</div>
          <div className="nao">Não</div>
        </BarraDaSecao>
        <ListaDePerguntas perguntas={VERIFICACAO_FABRICA.map((texto) => ({ texto }))} />
      </Secao>

      <Secao>
        <BarraDaSecao $comSimNao>
          <div className="titulo"><span>03</span> Verificação em obra</div>
          <div className="sim">Sim</div>
          <div className="nao">Não</div>
        </BarraDaSecao>
        <ListaDePerguntas perguntas={VERIFICACAO_OBRA} />
      </Secao>

      <Observacoes $flexivel>
        <Rotulo>Observações</Rotulo>
        <Pauta />
      </Observacoes>

      <Assinaturas>
        <div><Rotulo>Nome</Rotulo></div>
        <div><Rotulo>Assinatura</Rotulo></div>
      </Assinaturas>
      {via && via.numero > 1 && (
        <div style={{ position: "absolute", right: 40, bottom: 12, fontSize: 9.5, color: PAPEL.naoSeAplica }}>
          {via.numero}ª via · {via.data}
        </div>
      )}
    </Papel>
  );
}
